import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import usePushNotifications from '../../hooks/usePushNotifications';
import './NotificationSettings.css';

const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

const NotificationSettings = () => {
  const { isSupported, isSubscribed, permissionState, loading, error, subscribe, unsubscribe } =
    usePushNotifications();

  const [preferences, setPreferences] = useState({
    notifyFollows: true,
    notifyReactions: true,
    notifyGoalCompleted: true,
    defaultReminderTime: '',
  });
  const [prefLoading, setPrefLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const { data } = await api.get('/api/notification-preferences');
        setPreferences({
          notifyFollows: data.notifyFollows,
          notifyReactions: data.notifyReactions,
          notifyGoalCompleted: data.notifyGoalCompleted,
          defaultReminderTime: data.defaultReminderTime || '',
        });
      } catch (err) {
        console.error('Failed to fetch notification preferences', err);
      }
    };
    fetchPreferences();
  }, []);

  const handleToggle = (key) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setPrefLoading(true);
    setSaveMessage('');
    try {
      await api.put('/api/notification-preferences', preferences);
      setSaveMessage('Preferences saved!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save preferences', err);
      setSaveMessage('Failed to save. Please try again.');
    } finally {
      setPrefLoading(false);
    }
  };

  const toggleGroups = [
    {
      label: 'Social',
      items: [
        { key: 'notifyFollows', label: 'Someone follows you' },
        { key: 'notifyReactions', label: 'Someone reacts to your post' },
      ],
    },
    {
      label: 'Goals',
      items: [{ key: 'notifyGoalCompleted', label: 'Goal reminders' }],
    },
  ];

  const showIOSHint = isIOS() && !isInStandaloneMode();

  return (
    <div className="app-container">
      <div className="notification-settings-container">
        <h2>Notifications</h2>

        {/* iOS hint */}
        {showIOSHint && (
          <div className="notification-ios-hint">
            <p>
              📱 On iPhone or iPad, push notifications only work when the app is added to your Home
              Screen and opened from there. Open this page in <strong>Safari</strong>, tap the Share
              button, and select <strong>Add to Home Screen</strong>.
            </p>
          </div>
        )}

        {/* Push Toggle */}
        <div className="notification-push-section">
          <div className="notification-push-header">
            <div>
              <h3>Push Notifications</h3>
              <p>
                {!isSupported
                  ? 'Push notifications are not supported on this browser.'
                  : permissionState === 'denied'
                    ? 'Notifications are blocked. Please enable them in your browser settings.'
                    : isSubscribed
                      ? 'You are receiving push notifications.'
                      : 'Enable push notifications to stay up to date.'}
              </p>
            </div>
            {isSupported && permissionState !== 'denied' && (
              <button
                className={`push-toggle-btn ${isSubscribed ? 'active' : ''}`}
                onClick={isSubscribed ? unsubscribe : subscribe}
                disabled={loading}
              >
                {loading ? 'Loading...' : isSubscribed ? 'On' : 'Off'}
              </button>
            )}
          </div>
          {error && <p className="notification-error">{error}</p>}
        </div>

        {/* Preference Toggles — only show if subscribed */}
        {isSubscribed && (
          <>
            <div className="notification-groups">
              {toggleGroups.map((group) => (
                <div key={group.label} className="notification-group">
                  <h4>{group.label}</h4>
                  {group.items.map((item) => (
                    <div key={item.key} className="notification-row">
                      <label>{item.label}</label>
                      <div
                        className={`toggle-switch ${preferences[item.key] ? 'on' : ''}`}
                        onClick={() => handleToggle(item.key)}
                      >
                        <div className="toggle-thumb" />
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {preferences.notifyGoalCompleted && (
                <div className="notification-group">
                  <h4>Goal Reminder Times</h4>
                  <p className="notification-helper-text">
                    Reminder times are managed per goal from your profile Reminder Settings.
                  </p>
                </div>
              )}
            </div>

            <div className="notification-save-row">
              {saveMessage && <span className="save-message">{saveMessage}</span>}
              <button className="save-btn" onClick={handleSave} disabled={prefLoading}>
                {prefLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
